// Pure calculation functions for speedup visualization
// All latency values are in milliseconds

export interface SpeedupParams {
  targetLatencyMs: number;
  draftLatencyMs: number;
  k: number;
  acceptanceProb: number;
}

export interface SpeedupMetrics {
  sequentialIntervalMs: number;
  speculativeIntervalMs: number;
  expectedTokensPerCycle: number;
  effectiveRateMs: number;
  speedup: number;
}

/**
 * Calculate expected tokens per speculative decoding cycle.
 * Uses geometric series: 1 + α + α² + ... + αᵏ = (1 - α^(k+1)) / (1 - α)
 */
export function calculateExpectedTokens(k: number, acceptanceProb: number): number {
  if (acceptanceProb === 1) {
    return k + 1;
  }
  if (acceptanceProb === 0) {
    return 1;
  }
  return (1 - Math.pow(acceptanceProb, k + 1)) / (1 - acceptanceProb);
}

/**
 * Calculate all speedup metrics from params.
 */
export function calculateSpeedup(params: SpeedupParams): SpeedupMetrics {
  const { targetLatencyMs, draftLatencyMs, k, acceptanceProb } = params;

  const sequentialIntervalMs = targetLatencyMs;
  const speculativeIntervalMs = targetLatencyMs + k * draftLatencyMs;
  const expectedTokensPerCycle = calculateExpectedTokens(k, acceptanceProb);
  const effectiveRateMs = speculativeIntervalMs / expectedTokensPerCycle;
  const speedup = targetLatencyMs / effectiveRateMs;

  return {
    sequentialIntervalMs,
    speculativeIntervalMs,
    expectedTokensPerCycle,
    effectiveRateMs,
    speedup,
  };
}

/**
 * Sample tokens from one speculative decoding cycle.
 * Returns 1 to k+1 tokens based on acceptance probability.
 */
export function sampleSpeculativeTokens(k: number, acceptanceProb: number): number {
  let tokens = 1;
  for (let i = 0; i < k; i++) {
    if (Math.random() < acceptanceProb) {
      tokens++;
    } else {
      break;
    }
  }
  return tokens;
}
